import { useState, useEffect, useMemo } from 'react';
import type { StepType } from '@reactour/tour';
import React from 'react';
import { additionalContentService } from '@/services/additionalContentService';
import { useProfileStore } from '@/store/useProfileStore';
import { transformToTourSteps, type TourStepInput } from '@/services/tourService';
import type { AdditionalContent } from '@/types/additionalContent';
import { TourStepContent } from '@/components/tour/TourStepContent';

export interface UseGuidedTourOptions {
  page?: string;
  /** When set, fetch walkthrough steps for all pages, merge by order, and return stepsPageByIndex for cross-page navigation */
  pages?: string[];
  enabled?: boolean;
}

const WALKTHROUGH_SLOT_OWNER = 'walkthrough';
const WALKTHROUGH_SLOT_USER = 'walkthrough_user';

const OWNER_WALKTHROUGH_PAGES = ['dashboard', 'my-team', 'mystery-shop'];
const USER_WALKTHROUGH_PAGES = ['dashboard', 'mystery-shop', 'all-courses', 'my-progress', 'course/welcome'];

function isUserOnlyRole(profile: { roles?: string[] } | null): boolean {
  const roles = profile?.roles ?? [];
  return (
    roles.includes('user') &&
    !roles.includes('owner') &&
    !roles.includes('admin') &&
    !roles.includes('devops_admin')
  );
}

export function useGuidedTour({ page = 'dashboard', pages, enabled = true }: UseGuidedTourOptions = {}) {
  const profile = useProfileStore((state) => state.activeProfile);
  const [content, setContent] = useState<AdditionalContent[]>([]);
  const [loading, setLoading] = useState(true);

  const walkthroughSlot = isUserOnlyRole(profile)
    ? WALKTHROUGH_SLOT_USER
    : WALKTHROUGH_SLOT_OWNER;

  const pagesToUse = (pages?.length ? pages : walkthroughSlot === WALKTHROUGH_SLOT_USER ? USER_WALKTHROUGH_PAGES : OWNER_WALKTHROUGH_PAGES);

  useEffect(() => {
    if (!enabled || !profile?.id) {
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        setLoading(true);
        if (pagesToUse.length) {
          const results = await Promise.all(
            pagesToUse.map((p) =>
              additionalContentService.searchAdditionalContent({ page: p, slot: walkthroughSlot })
            )
          );
          const merged = results.flat();
          merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setContent(merged);
        } else {
          const data = await additionalContentService.searchAdditionalContent({
            page,
            slot: walkthroughSlot,
          });
          setContent(data);
        }
      } catch {
        setContent([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [page, profile?.id, enabled, pagesToUse.length, pagesToUse.join(','), walkthroughSlot]);

  const stepInputs = useMemo<TourStepInput[]>(
    () => transformToTourSteps(content, pagesToUse.length ? undefined : page),
    [content, page, pagesToUse]
  );

  const stepsPageByIndex = useMemo<string[]>(
    () => stepInputs.map((input) => input.additionalContent.page),
    [stepInputs]
  );

  const steps = useMemo<StepType[]>(
    () =>
      stepInputs.map((input) => ({
        selector: input.selector,
        position: input.position,
        resizeObservables: ['ion-content', 'ion-page'],
        content: React.createElement(TourStepContent, {
          item: input.additionalContent,
        }),
      })),
    [stepInputs]
  );

  const tourCompletionItem = useMemo(
    () =>
      stepInputs.length ? stepInputs[stepInputs.length - 1].additionalContent : undefined,
    [stepInputs]
  );

  type DoNotShowKey = 'walkthrough' | 'walkthrough_user';
  const shouldAutoStart =
    !!profile &&
    !profile.metadata?.doNotShow?.[walkthroughSlot as DoNotShowKey] &&
    !loading &&
    steps.length > 0;

  return {
    steps,
    loading,
    content,
    tourCompletionItem,
    shouldAutoStart,
    hasSteps: steps.length > 0,
    stepsPageByIndex,
    walkthroughSlot,
  };
}
